/* eslint-disable react-hooks/exhaustive-deps */
import { Checkbox, HeaderMenu, TableSection, Button } from "components"
import { APPROVAL } from "pages/Sales/Register/utils/vars"
import { DropdownCustomer } from "pages/Sales/Transaction/CustomerOrder/components"
import {
  Controller,
  useFieldArray,
  useFormContext,
  useWatch
} from "react-hook-form"
import toast from "react-hot-toast"
import { IoSendSharp } from "react-icons/io5"
import { MdPayment } from "react-icons/md";
import { useNavigate } from "react-router-dom"
import { useToggle } from "utils"
import { FormInvoiceType } from "../types/FormInvoice"
import { useDeliveryNo } from "../utils/hooks"
import { CodeInvoice } from "./CodeInvoice"
import { CommandButtons } from "./CommandButtons"
import { FormInvoice } from "./FormInvoice"
import { ModalDelete } from "./ModalDelete"
import { SummaryFooter } from "./SummaryFooter"
import { TableSalesOrderInvoice } from "./Tables"
import { calculateSummaryInvoice } from "../utils/functions"

export type FormSectionProps = {
  approvalView?: boolean
  isEdit?: boolean
  onSubmit: () => void,
  isDetailShop?: boolean
}

export const FormSection = ({
  approvalView,
  isEdit = false,
  onSubmit,
  isDetailShop
}: FormSectionProps) => {
  const navigate = useNavigate()
  const modalDelete = useToggle(false)
  const methods = useFormContext<FormInvoiceType>()
  const [
    invoice_id,
    approval_status,
    sales_order_id,
    transaction_date,
    trx_code,
    customer_id,
    customer_name,
    so_payment_status_text,
    freight,
    total_payment,
  ] = useWatch({
    control: methods.control,
    name: [
      "invoice_id",
      "approval_status",
      "sales_order_id",
      "transaction_date",
      "trx_code",
      "customer_id",
      "customer_name",
      "so_payment_status_text",
      "freight",
      "total_payment"
    ]
  })

  const invoice_detail = useFieldArray({
    control: methods.control,
    name: "invoice_detail"
  })

  const sodetail = useFieldArray({
    control: methods.control,
    name: "sodetail"
  })

  const detailInvoice: FormInvoiceType["invoice_detail"] = methods.getValues("invoice_detail");
  const summary = calculateSummaryInvoice({
    invoice_detail:detailInvoice,
    freight:freight,
    total_payment:total_payment
  })

  const deleteData = {
    invoice_id: invoice_id ?? 0,
    customer_id: customer_id ?? 0,
    transaction_date,
    trx_code: trx_code ?? "",
    customer_name: customer_name ?? "",
    approval_status: approval_status ?? 1
  }

  useDeliveryNo({
    enabled: !isEdit && Boolean(sales_order_id),
    onSuccess: (data) => methods.setValue("delivery_no", data)
  })

  return (
    <section className="container my-5 flex flex-col gap-5">
      <HeaderMenu title="DATA ENTRY | DELIVERY INVOICE">
        <CodeInvoice isEdit={isEdit} />
      </HeaderMenu>

      <section className="overflow-x-auto max-h-[calc(100vh-350px)]">
        <section className="grid lg:grid-cols-3 gap-x-6 gap-y-3">
          <section className="w-full">
            <DropdownCustomer approvalView={approvalView} isEdit />
          </section>

          <section>
            <div className="normal-case flex justify-evenly items-center pt-10">
              <div className="flex flex-col gap-2">
                <span>TRADING TERM </span>
                <span>PRICE LEVEL</span>
              </div>

              <div className="px-2 flex flex-col gap-2">
                <IoSendSharp />
                <IoSendSharp />
              </div>

              <div className="flex flex-col gap-2">
                <Controller
                  control={methods.control}
                  name="payment_term_name"
                  render={({ field }) => (
                    <span>Net {field.value ?? 0} Days after OEM </span>
                  )}
                />

                <Controller
                  control={methods.control}
                  name="price_level_name"
                  render={({ field }) => (
                    <span>{field.value ? field.value : "-"}</span>
                  )}
                />
              </div>
            </div>
          </section>

          <section className="lg:justify-self-end self-end mb-3">
            <Controller
              control={methods.control}
              name="tax_inclusive"
              render={({ field }) => (
                <Checkbox disabled value={field.value} label="">
                  TAX-INCLUSIVE
                </Checkbox>
              )}
            />
          </section>
        </section>

        <FormInvoice approvalView={approvalView} />

        <TableSalesOrderInvoice
          approvalView={approvalView}
          data={invoice_detail.fields}
          displayArrowNavigation={false}
          displayStockOnHand={true}
          onClickDeleteSOInvoice={(key: number) => {
            invoice_detail.remove(key)
          }}
          isDetailShop={isDetailShop}
        />

        <section className="grid lg:grid-cols-2 gap-x-6 gap-y-3 mt-[30px] mb-[30px]">
          <div className="flex flex-col gap-3 flex-grow">
            <TableSection textLeft>
              <TableSection.Row
                className="uppercase"
                label="INVOICE STATUS"
                value={`${methods.getValues("invoice_status_text")?.toString() ?? "-"}`}
              />
              <TableSection.Row
                className="uppercase"
                label="PAYMENT STATUS"
                value={`${so_payment_status_text ?? "-"}`}
              />
              <TableSection.Row
                label="APPROVAL STATUS"
                value={`${APPROVAL[approval_status] ?? "-"}`}
              />
              <TableSection.Row
                label="APPROVED BY"
                value={`${methods.getValues("approved_by_name") ?? "-"}`}
              />
            </TableSection>
          </div>

          <div className="flex flex-col lg:items-end gap-3">
            <SummaryFooter />
          </div>
        </section>

        {!isDetailShop && (
          <>
            <TableSalesOrderInvoice
              display
              approvalView={approvalView}
              showActionColumn={false}
              data={sodetail.fields}
              displayArrowNavigation={true}
              displayStockOnHand={false}
              onClickArrowNavigation={(item) => {
                const isExist = invoice_detail?.fields?.find(
                  (inv) => inv.product_name === item.product_name
                )

                const hasStock = (item?.soh ?? 0) > 0

                if (!isExist && hasStock) {
                  return invoice_detail.append(item)
                }

                toast.error(
                  isExist
                    ? "Sales Order Detail is already on Invoice Detail List"
                    : "Stock is not available"
                )
              }}
            />
            <CommandButtons
              approvalView={approvalView}
              showSaveButton={approval_status !== APPROVAL.APPROVED}
              showDeleteButton={isEdit && approval_status !== APPROVAL.APPROVED}
              onDelete={() => modalDelete.setActive(true)}
              onSave={onSubmit}
              customElements={(
                <Button
                  color="primary"
                  className={`flex flex-col h-16 bg-base-200 border-[1px] border-base-300 px-0 w-35 hover:bg-primary hover:text-black border-white ${(summary.payment >= summary.totalAmount || approvalView) ? "hidden" : "block"}`}
                  onClick={() =>
                    navigate(
                      `/sales/transaction/payment-invoice/${invoice_id}`
                    )
                  }>
                  <MdPayment className="size-[24px]" />
                  INVOICE PAYMENT
                </Button>
              )}
            />

            <ModalDelete
              modalState={modalDelete}
              data={deleteData}
              onSuccess={() => {
                modalDelete.setActive(false);
                navigate("/sales/register", { replace: true });
              }}
            />
          </>
        )}
      </section>
    </section>
  )
}
